import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchOrders } from '../store/slices/orderSlice';
import { showNotification } from '../store/slices/notificationPopupSlice';
import { API_BASE_URL } from '../config/api';
import { Capacitor } from '@capacitor/core';
import { playNotificationSound } from '../utils/notificationSound';

export const useOrderNotifications = (restaurantId) => {
  const dispatch = useDispatch();
  const eventSourceRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const notificationIdRef = useRef(1000);

  useEffect(() => {
    if (!restaurantId) return;

    // Initial fetch
    dispatch(fetchOrders({ restaurantId, limit: 15 }));

    // Poll orders every 5 seconds so owner UI always stays fresh
    // (even if SSE is blocked by network / browser / proxy).
    const pollMsRaw = process.env.REACT_APP_ORDER_NOTIFICATIONS_POLL_MS;
    const pollMsParsed = pollMsRaw ? parseInt(pollMsRaw, 10) : NaN;
    const pollMs =
      Number.isFinite(pollMsParsed) && pollMsParsed >= 1000
        ? pollMsParsed
        : 5000;
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(() => {
      dispatch(fetchOrders({ restaurantId, limit: 15 }));
    }, pollMs);

    const buildOrderMessage = (order) => {
      if (!order) return 'A new order has been placed.';
      const tableStr = order.tableId?.tableNumber ? `Table ${order.tableId.tableNumber}` : 'Counter order';
      const amount = order.finalAmount != null ? `₹${Number(order.finalAmount).toFixed(2)}` : '';
      const customer = order.customerName || 'Customer';
      return `${tableStr} • ${customer}${amount ? ` • ${amount}` : ''}`;
    };

    const showNativeHeadsUp = async (title, body, orderId) => {
      if (!Capacitor.isNativePlatform()) return;
      try {
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        const id = Math.abs(notificationIdRef.current++) % 2147483647;
        await LocalNotifications.schedule({
          notifications: [{
            id,
            title: title || 'RestroAI',
            body: body || 'New order',
            sound: 'default',
            channelId: 'restroai_orders',
            extra: { orderId: orderId || '' },
            autoCancel: true,
          }],
        });
      } catch (e) {
        console.log('Local notification failed:', e);
      }
    };

    // Set up Server-Sent Events for real-time notifications
    const setupSSE = () => {
      try {
        const url = `${API_BASE_URL}/orders/notifications/${restaurantId}`;
        eventSourceRef.current = new EventSource(url);

        eventSourceRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'NEW_ORDER') {
              const order = data.data || {};
              const orderId = order._id != null ? String(order._id) : null;
              const message = buildOrderMessage(order);
              playNotificationSound();
              // In-app popup + Redux (so modal can open on tap)
              dispatch(showNotification({
                title: 'New Order',
                message,
                orderId,
              }));
              // Native heads-up notification (Android) – uses restroai_orders channel (sound enabled)
              showNativeHeadsUp('RestroAI – New Order', message, orderId);
              dispatch(fetchOrders({ restaurantId, limit: 15 }));
            }
          } catch (err) {
            console.error('SSE parse error:', err);
          }
        };

        eventSourceRef.current.onerror = (error) => {
          console.error('SSE connection error:', error);
          eventSourceRef.current?.close();
          eventSourceRef.current = null;
        };
      } catch (error) {
        console.error('Failed to setup SSE:', error);
      }
    };

    setupSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [restaurantId, dispatch]);
};